type Listener = (...args: any[]) => void

export class Emitter {
  private listenersMap: Map<string, Listener[]>
  private onceListenersMap: Map<string, Listener[]>

  constructor() {
    this.listenersMap = new Map()
    this.onceListenersMap = new Map()
  }

  private _getListeners(eventName: string): Listener[] {
    return this.listenersMap.get(eventName) || []
  }

  private _getAllListeners(): [string, Listener[]][] {
    // Return only entries that have at least 1 listener
    return Array.from(this.listenersMap.entries()).filter((o) => o[1].length)
  }

  private _getOnceListeners(eventName: string): Listener[] {
    return this.onceListenersMap.get(eventName) || []
  }

  public on(eventName: string, listener: Listener): this {
    const listeners = this._getListeners(eventName)
    if (listeners.indexOf(listener) < 0) {
      const newListeners = listeners.concat(listener)
      this.listenersMap.set(eventName, newListeners)
    }
    return this
  }

  public once(eventName: string, listener: Listener): void {
    const listeners = this._getOnceListeners(eventName)
    if (listeners.indexOf(listener) < 0) {
      const newListeners = listeners.concat(listener)
      this.onceListenersMap.set(eventName, newListeners)
    }
  }

  public emit(eventName: string, ...args: any[]): void {
    this._getListeners(eventName).forEach((l) => l(...args))
    this._getOnceListeners(eventName).forEach((l) => l(...args))
    this.onceListenersMap.delete(eventName)
  }

  public removeAllListeners(eventName?: string): void {
    if (eventName) {
      this.listenersMap.delete(eventName)
      this.onceListenersMap.delete(eventName)
    } else {
      // Remove all subscriptions
      this.listenersMap.clear()
      this.onceListenersMap.clear()
    }
  }

  public removeListener(eventName: string, listener: Listener): void {
    const listeners = this._getListeners(eventName)
    this.listenersMap.set(
      eventName,
      listeners.filter((l) => l !== listener)
    )
    const onceListeners = this._getOnceListeners(eventName)
    this.onceListenersMap.set(
      eventName,
      onceListeners.filter((l) => l !== listener)
    )
  }
}
